import { useState } from 'react';
import type { ConversationItem } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { useAddContentAttachment } from '@/hooks/useContentAttachmentQueries';
import { useContentCreate } from '@/hooks/useContentQueries';
import { Anchor, Check, Plus } from 'lucide-react';

type AnchorProposalReadyItem = Extract<ConversationItem, { kind: 'anchor_proposal_ready' }>;

export interface ProvenanceAnchorProposalCardProps {
  item: AnchorProposalReadyItem;
}

export function ProvenanceAnchorProposalCard({ item }: ProvenanceAnchorProposalCardProps) {
  const [selectedAnchorId, setSelectedAnchorId] = useState<number | null>(null);
  const [attachedTo, setAttachedTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const createContentMutation = useContentCreate();
  const addAttachmentMutation = useAddContentAttachment();
  const busy = createContentMutation.isPending || addAttachmentMutation.isPending;

  const attachTo = async (anchorContentId: number, anchorTitle: string) => {
    await addAttachmentMutation.mutateAsync({
      contentId: anchorContentId,
      payload: { childContentId: item.documentContentId },
    });
    setAttachedTo(anchorTitle);
  };

  const run = (action: () => Promise<void>) => {
    setError(null);
    void action().catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : 'Could not file the document. Try again.');
    });
  };

  const createAndAttach = async () => {
    const created = await createContentMutation.mutateAsync({
      contentType: 'ANCHOR',
      memberId: 0,
      title: item.suggestedTitle,
      ...(item.suggestedDescription !== undefined ? { description: item.suggestedDescription } : {}),
      ...(item.originProvenanceContentId !== undefined
        ? { originProvenanceContentId: item.originProvenanceContentId }
        : {}),
    });
    await attachTo(created.id, item.suggestedTitle);
  };

  if (attachedTo !== null) {
    return (
      <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 shrink-0" />
          <p className="min-w-0 truncate">
            Filed under <span className="font-medium">{attachedTo}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <Anchor className="h-4 w-4 shrink-0" />
        <p className="font-medium">File this document under an Anchor?</p>
      </div>
      <p className="mt-1 min-w-0 truncate text-xs text-muted-foreground">{item.documentTitle}</p>

      {item.candidates.length > 0 ? (
        <div className="mt-2 space-y-1">
          {item.candidates.map((candidate) => (
            <button
              key={candidate.contentId}
              type="button"
              disabled={busy}
              onClick={() => setSelectedAnchorId(candidate.contentId)}
              className={`block w-full rounded border px-2 py-1.5 text-left text-xs transition-colors ${
                selectedAnchorId === candidate.contentId
                  ? 'border-foreground bg-muted/60'
                  : 'border-border hover:bg-muted/40'
              }`}
            >
              <span className="block min-w-0 truncate font-medium">{candidate.title || candidate.code}</span>
              {candidate.description ? (
                <span className="block min-w-0 truncate text-muted-foreground">{candidate.description}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy || selectedAnchorId === null}
          onClick={() => {
            const chosen = item.candidates.find((candidate) => candidate.contentId === selectedAnchorId);
            if (chosen) {
              run(() => attachTo(chosen.contentId, chosen.title || chosen.code));
            }
          }}
        >
          Attach to selected
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => run(createAndAttach)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Create &ldquo;{item.suggestedTitle}&rdquo;
        </Button>
      </div>
      {error !== null ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
